import { showErrToast } from "../components/Toasts";
import React from "react";
import BaseModel from "../models/BaseModel";
import type Room from "../models/Room";
import type Group from "../models/Group";
import Page from "../models/Page";
import RoomDialog from "../modelDialogs/RoomDialog";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import type Floor from "../models/Floor";
import RoomPropertyView from "../modelPropertyViews/RoomPropertyView";
import Logger from "../Logger";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import { buttonItems, permanentPowerItems, consumerItems, sensorItems, distributionItems } from "../components/Toolbar";
import type Consumer from "../models/Consumer";
import type DeviceTemplate from "../models/DeviceTemplate";
import type Fuse from "../models/Fuse";
import Point from "../models/Point";
import { showOnboarding } from "../onBoarding/OnBoardingWindow";
import { itx, noItx } from "../translationItx/itx";
import { WorkingPane } from "../models/ActivePanes";
import { PageTypes } from "../models/PageTypes";
import { DeviceClasses } from "../models/DeviceClasses";
import { EventKey } from "../eventLogger/EventKeys";
import { ApplicationMode } from "../models/SettingsEnums";
import { isUiModeDeveloper } from "../uiMode";
import {
  buildC1DefinitionAddMenu,
  getC1ContainerDefinition,
} from "../../ConceptOne/src/model/C1_NavigatorDefinitionMenu";
import {
  isIndependentlyPlacedC1Object,
  repositionAfterRoomMove,
} from "../../ConceptOne/src/model/C1_NavigatorRoomMove";

const logger = new Logger();

type SortableModelCollection = {
  sortBy: (property: string) => BaseModel[];
};

/** Appends a sorted child collection when it is already available during model loading. */
function appendSortedChildren(target: BaseModel[], collection: SortableModelCollection | undefined): void {
  if (!collection) return;
  const children = collection.sortBy("tagOrName");
  for (const child of children) target.push(child);
}

export default class RoomTreeViewModel extends TreeViewModel {
  get typedObj(): Room {
    return this.obj as Room;
  }

  get isFolder(): boolean {
    return true;
  }

  getNameNeeded(): boolean {
    return !this.isFloorGeneral();
  }

  getTagNeeded(): boolean {
    return !this.isFloorGeneral();
  }

  get childCollection(): Array<BaseModel> | undefined {
    if (state.flattenGroups.value) {
      let res: Array<BaseModel> = this.typedObj.allConsumers();
      res = res.concat(this.typedObj.allButtons);
      return res;
    } else {
      const res: BaseModel[] = [];
      appendSortedChildren(res, this.typedObj.groups);
      appendSortedChildren(res, this.typedObj.consumers);
      appendSortedChildren(res, this.typedObj.buttons);
      return res;
    }
  }

  get iconUrl(): string | undefined {
    if (this.typedObj) {
      return "lab la-codepen";
    }

    return undefined;
  }

  isFloorGeneral(): boolean {
    return this.typedObj.tag == "" && this.typedObj.name == "";
  }

  getFullTitle(isExpanded: boolean): string | undefined | JSX.Element {
    if (this.isFloorGeneral()) {
      return <div style={{ fontStyle: "italic" }}>{itx("roomTreeViewModel.generalFloor", "Etage allgemein")}</div>;
    }
    return undefined;
  }

  getPropertyControl(setPinned: (pinned: boolean) => void): any {
    if (this.typedObj.tag.startsWith("@I")) {
      return undefined;
    }

    if (this.isFloorGeneral()) {
      return super.getPropertyControl(setPinned);
    }
    return <RoomPropertyView obj={this.obj} setPinned={setPinned} />;
  }

  handleOpen(transitOpen: boolean = false): boolean {
    if (this.isFloorGeneral()) {
      return false;
    }

    const startPoint = this.typedObj.c1?.data.startPoint;
    if ((state.projectMetadata.isC1Project || this.typedObj.c1) &&
        (!startPoint || !Number.isFinite(startPoint.x) || !Number.isFinite(startPoint.y))) {
      showErrToast(itx("c1.room.notPlaced", "Dieser Raum ist noch nicht platziert. Bitte platziere ihn zunächst im Etagenplan."));
      return false;
    }
    const isC1Room = this.typedObj.c1 !== undefined;
    const pageId = isC1Room ? this.typedObj.id : (this.typedObj as Room).getOrCreatePage().id;

    // super.handleOpenId(pageId, transitOpen);

    let pane: WorkingPane | undefined;

    if (state.activePanes.isOpen(pageId)) {
      pane = state.activePanes.activatePane(pageId);
    } else {
      pane = state.activePanes.loadPage(pageId);
    }

    if (pane) {
      if (!pane.isTransitOpen) {
        //
        // if we open a second time,
        // iE click 2x Enter in the tree its premanent open
        //
        pane.isTransitOpen = transitOpen;
      } else {
        pane.isTransitOpen = false;
      }
      // pane.tabItemData?.raiseOnChange("isTransitOpen")
    }

    showOnboarding("consumerBar"); // no itx
    return true;
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  buildAddConsumerMenuItems(point?: Point): any {
    const definitionMenu = buildC1DefinitionAddMenu(this.typedObj, getC1ContainerDefinition(this.typedObj));
    if (!isUiModeDeveloper() || state.settings.applicationMode === ApplicationMode.ConceptOne) return definitionMenu;

    const items: any[] = [];
    let i = 0;
    const tbi = consumerItems.concat(buttonItems, permanentPowerItems, sensorItems);
    tbi.forEach((item) => {
      const imageStyle: any = { width: "auto", height: "25px", objectFit: "contain" };
      const img = <img draggable="false" style={imageStyle} src={item.image} />; // no itx
      items.push({
        id: `item${i++}`,
        image: img,
        label: (item as any).titleShort,
        onClick: () => {
          const page = this.typedObj.getOrCreatePage();
          if (page) {
            const pPX = point || page.findFreePositionForNewObject();
            if (
              (item as any).classification?.name == "Button" ||
              (item as any).classification?.name == "SensorNew" ||
              (item as any).classification?.name == "DecentralActor"
            ) {
              const button = page.createNewButtonInPage(this.typedObj, item, pPX);
              if ((item as any).classification?.name == "SensorNew" && button) {
                button.classification = DeviceClasses.SensorNew;
              } else if ((item as any).classification?.name == "DecentralActor" && button) {
                button.classification = DeviceClasses.DecentralActor;
              }
              if (button) {
                state.eventLogger.addEvent(EventKey.ObjectCreated, {
                  objectType: "Button",
                  obj: button.id + "_" + button.tag + "_" + button.name,
                });
              }
              state.selectionManager.selectedObject = button;
            } else {
              page.createNewConsumerInPage(this.typedObj, item, pPX).then((consumer) => {
                if (consumer) {
                  state.eventLogger.addEvent(EventKey.ObjectCreated, {
                    objectType: "Consumer" + (consumer.isDali ? "Dali" : "") + consumer.channelType,
                    obj: consumer.id + "_" + consumer.tag + "_" + consumer.name,
                  });
                }
                state.selectionManager.selectedObject = consumer;
              });
            }
          }
        },
      });
    });
    return {
      ...definitionMenu,
      items: [...definitionMenu.items, {
        id: "room-developer-add-menu",
        label: itx("roomTreeViewModel.developerObjects", "Entwickler: weitere Objekte"),
        iconName: "code",
        menu: { title: itx("roomTreeViewModel.developerObjects", "Entwickler: weitere Objekte"), items },
      }],
    };
  }

  handleAddWithEvent(event: any) {
    this.handleAddConsumer(event);
  }

  handleAddConsumer(event: any, point?: Point) {
    const menuItems = this.buildAddConsumerMenuItems(point);
    state.contextMenuManager.show1(event, menuItems, menuItems.disabledItemIds ?? []);
  }

  getButtonsAsContextMenuItems(): any[] {
    // Return all auto-derived button items, but replace the "plus-circle" button with
    // a native Submenu so clicking it shows a side panel instead of a replacement menu.
    return this.buttonsAsContextMenuItems().map((item) => {
      if (item.id === "btn_plus-circle") {
        return {
          ...item,
          menu: this.buildAddConsumerMenuItems(),
          onClick: undefined,
        };
      }
      return item;
    });
  }

  inlineButton_Add() {
    return (
      <TreeMenuIcon
        onClick={(ev: any) => {
          this.handleAddConsumer(ev);
        }}
        iconName="plus-circle"
        tooltipKey="inlineButton.addFunctionButtonConsumer" // no itx
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "autoConnect"} // no itx
      />
    );
  }

  inlineButton_RoomDiagnostics() {
    return (
      <TreeMenuIcon
        onClick={(ev: any) => {
          const id = this.obj.id;

          if (
            state.activePanes.isOpen(id + ":diagnostics") // no itx
          ) {
            state.activePanes.activatePane(id + ":diagnostics"); // no itx
          } else {
            state.activePanes.openPane(this.obj);
          }
          return true;
        }}
        iconName="tasks" // no itx
        tooltipText={itx("roomTreeViewModel.diagnostic", "Diagnose")}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "diagnose"} // no itx
      />
    );
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //super.inlineButton_Properties(),
  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons(): JSX.Element[] | undefined {
    if (this.typedObj.tag.startsWith("@I")) {
      const res = [
        <TreeMenuIcon
          onClick={async () => {
            //
            // hier mit import für lazy loading, getrenntes bundle für ets import
            //
            const module = await import("../extensions/imports/ets/postImport/moveObjects");
            const CreateObjectsFromImport = module.default;
            state.dialogManager.openDialog(<CreateObjectsFromImport rootObject={this.typedObj} />, false, false);
          }}
          iconName="star" // no itx
          // tooltipKey={tooltipKey}
          //          tooltipText={itx("deviceTreeViewModel.syncETS", "Sync ETS")}
          objName="CreateObjects" // no itx
          key="CreateObjects"
        />,
      ];

      return res;
    } else {
      if (state.settings.applicationMode === ApplicationMode.ConceptOne) {
        return [this.inlineButton_Add()];
      } else if (state.settings.diagnosticMode) {
        return [super.inlineButton_Open("inlineButton.editRoom"), this.inlineButton_RoomDiagnostics()]; // no itx
      } else {
        if (this.isFloorGeneral()) {
          return [this.inlineButton_Add()];
        } else {
          return [super.inlineButton_Open("inlineButton.editRoom"), this.inlineButton_Add()]; // no itx
        }
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  handleProperties() {
    state.dialogManager.openDialog(<RoomDialog parent={this.obj.parent as Floor} obj={this.typedObj} />);
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (
      copiedObjClassName == "Room" ||
      copiedObjClassName == "Floor" ||
      copiedObjClassName == "Consumer" ||
      copiedObjClassName == "Button" ||
      copiedObjClassName == "Actor"
    ) {
      return true;
    }
    return false;
  }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();

    if (
      (copiedObjClassName && copiedObjClassName == "Room") ||
      (copiedObjClassName && copiedObjClassName == "Floor") ||
      (copiedObjClassName && copiedObjClassName == "Consumer") ||
      (copiedObjClassName && copiedObjClassName == "Button") ||
      (copiedObjClassName && copiedObjClassName == "Actor")
    ) {
      return new Array<string>();
    }

    return ["paste"]; // no itx
  }

  get contextMenuItems(): any {
    let subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData?.items?.[0]?.name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      title: this.obj?.name,
      items: [
        this.baseContextMenuItems_Copy(),
        // this.baseContextMenuItems_Cut(),
        this.baseContextMenuItems_Paste(subLabelPaste),
        this.baseContextMenuItems_Delete(),
        // this.baseContextMenuItems_Properties(),
      ],
    };
  }

  allowDrag(): boolean {
    return true;
  }

  allowDrop(event: any): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    try {
      const room = this.typedObj;

      if (!this.typedObj.page) {
        this.typedObj.page = new Page({}, this.typedObj);
      }

      const page = room.page;

      if (page) {
        const pPX = page.findFreePositionForNewObject();
        //create new Button in Room from Template

        if (mode == DragMode.copy && obj.className != "DeviceTemplate") {
          // copy object
          await state.menubarActions.onCopy(obj);
          await state.menubarActions.onPaste(this.typedObj);
          return new DropResult(true);
        } else {
          // move or copy button to this room

          switch (obj.className) {
            case noItx("Button"):
            case noItx("Consumer"): {
              //   let errorMessage = itx(
              //   "floorTreeViewModel.relocationFromT1ToT2NotAllowed",
              //   "Das verschieben von T1 in die Etage T2 ist nicht erlaubt!",
              //   { T1: obj.tag + " " + obj.name, T2: this.typedObj.tag + " " + this.typedObj.name },
              // );
              // return new DropResult(false, errorMessage);
              const consumer = obj as Consumer;
              const alreadyInRoom = consumer.parentRoom === room;
              const moved = state.undoManager.runAsSingleStep(
                itx("roomTreeViewModel.moveToRoom", `T1 nach T2 verschieben`, {
                  T1: `${consumer.fullTag} ${consumer.name}`.trim() || consumer.className,
                  T2: `${room.fullTag} ${room.name}`.trim() || room.className,
                }),
                () => {
                  const didMove = consumer.moveToNewParent(room);
                  if (didMove && !alreadyInRoom) repositionAfterRoomMove(consumer, room);
                  return didMove;
                },
              );
              if (moved) {
                state.selectionManager.selectedObject = consumer;
                return new DropResult(true);
              } else {
                return new DropResult(false);
              }
            }

            case noItx("Group"): {
              const group = obj as Group;
              if (!isIndependentlyPlacedC1Object(group)) {
                return new DropResult(
                  false,
                  itx(
                    "roomTreeViewModel.cannotMoveGroup",
                    "Dieses Objekt ist Teil der Ausstattungsdefinition und kann nicht einzeln in einen anderen Raum verschoben werden.",
                  ),
                );
              }
              if (group.parentRoom === room) {
                return new DropResult(true);
              }
              const moved = state.undoManager.runAsSingleStep(
                itx("roomTreeViewModel.moveToRoom", `T1 nach T2 verschieben`, {
                  T1: `${group.fullTag} ${group.name}`.trim() || group.className,
                  T2: `${room.fullTag} ${room.name}`.trim() || room.className,
                }),
                () => {
                  const didMove = group.moveToNewParent(room);
                  if (didMove) repositionAfterRoomMove(group, room);
                  return didMove;
                },
              );
              if (moved) {
                state.selectionManager.selectedObject = group;
                return new DropResult(true);
              }
              return new DropResult(false);
            }

            case noItx("DeviceTemplate"):
              const isActor = obj && obj.className == "DeviceTemplate" && (obj as DeviceTemplate).isActor;

              const button = page.createNewButtonInPage(room, obj, pPX);
              if (button) {
                if (!isActor) {
                  //// check is Actor is allowed to be used decentral !?!?!.....
                }
                state.selectionManager.selectedObject = button;
                return new DropResult(true);
              } else {
                return new DropResult(false, `Drag&Drop ist nicht erlaubt für Aktoren`);
              }

              break;

            case noItx("Page"):
              const pageDraged = obj as Page;
              if (pageDraged.documentType.type == PageTypes.pdf) {
                // set here the backgroundImgUrl
                const dpmm = 30;
                const backgrounimageBefore = page.backgroundImgUrl;
                await pageDraged.pdfFile
                  .convertPdfToDataUrl({
                    pageNumber: 1,
                    targetWidth: page.pagesize.width * dpmm,
                    targetHeight: page.pagesize.height * dpmm,
                    rotation: page.pdfRotation || 0,
                  })
                  .then((imgUrl) => {
                    page.resizeImageAfterLoad(imgUrl).then((result) => {
                      page.backgroundImgUrl = result as string;
                    });
                  });

                page.raiseOnChange();
                return new DropResult(true);
              }
              break;
            case noItx("Fuse"):
              this.typedObj.conectAll230ConsumersWihFuse(obj as Fuse);
              return new DropResult(true);
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
    return new DropResult(false);
  }

  async handleDrop(mode: DragMode, sData: string) {
    try {
      const data = JSON.parse(sData);
      if (data.id) {
        super.handleDrop(mode, sData);
      } else {
        logger.log("onDrop", data);

        const room = this.typedObj;
        const page = room.page;

        if (page) {
          const pPX = page.findFreePositionForNewObject();
          //create this Consumer in Room

          page
            .createNewConsumerInPage(room, data, pPX)
            .then((consumer) => (state.selectionManager.selectedObject = consumer));
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
  }
}
